import { parseFullOBIS } from '@enmon/commons/lib/enmon';
import type { PartialInventorizationExtraction } from '@/shared/ocr/inventorize-extraction-contract';

export function readExtractionSuggestion(
  extraction: PartialInventorizationExtraction,
  path: string,
  allowedValues?: readonly string[],
) {
  const rawValue = readExtractionValue(extraction, path);
  const value = stringifyExtractionValue(rawValue);

  if (!(value && isAllowedValue(value, allowedValues))) {
    return undefined;
  }

  return {
    confidence: readExtractionConfidence(extraction, path),
    value,
  };
}

function isAllowedValue(value: string, allowedValues: readonly string[] | undefined) {
  return allowedValues === undefined || allowedValues.includes(value);
}

function readExtractionValue(extraction: PartialInventorizationExtraction, path: string) {
  const registerObisMatch = /^registers\.(\d+)\.(obis|tariff)$/.exec(path);

  if (registerObisMatch) {
    const index = registerObisMatch[1];
    const key = registerObisMatch[2] as 'obis' | 'tariff';
    const parsed = parseFullOBIS(stringifyExtractionValue(readPath(extraction, `registers.${index}.obis`)));

    if (parsed) {
      return parsed[key];
    }
  }

  return readPath(extraction, path);
}

function readExtractionConfidence(extraction: PartialInventorizationExtraction, path: string) {
  const rootPath = path.split('.')[0];

  return (
    extraction.fieldConfidence?.[path] ??
    extraction.fieldConfidence?.[path.replace('.0.', '.')] ??
    (rootPath ? extraction.fieldConfidence?.[rootPath] : undefined)
  );
}

function readPath(source: unknown, path: string) {
  return path.split('.').reduce<unknown>((value, segment) => {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (Array.isArray(value)) {
      const index = Number(segment);

      return Number.isInteger(index) ? value[index] : undefined;
    }

    if (typeof value === 'object') {
      return (value as Record<string, unknown>)[segment];
    }

    return undefined;
  }, source);
}

function stringifyExtractionValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map(stringifyExtractionValue).filter(Boolean).join(', ');
  }

  if (typeof value === 'object') {
    if ('text' in value && typeof (value as { text?: unknown }).text === 'string') {
      return (value as { text: string }).text;
    }

    if ('id' in value && typeof (value as { id?: unknown }).id === 'number') {
      return String((value as { id: number }).id);
    }

    return '';
  }

  return String(value);
}
